import React from 'react';
import { Box, Typography, Select, MenuItem, SelectChangeEvent } from '@mui/material';
import Grid2 from '@mui/material/Grid2';
import { PaymentDetails, CurrencyOption } from '../collectPayment.types';
import { CURRENCIES } from '../collectPayment.constants';
import { formatCurrency } from '../collectPayment.helper';

interface PaymentDetailsCardProps {
  details: PaymentDetails;
  onCurrencyChange?: (currency: string) => void;
  currencyOptions?: CurrencyOption[];
}

const LABELS = {
  cardTitle: 'Payment Details',
  date: 'Date',
  patientName: 'Patient Name',
  patientId: 'MRN No.',
  currency: 'Currency',
  netAmount: 'Net Amount',
};

const PaymentDetailsCard: React.FC<PaymentDetailsCardProps> = ({
  details,
  onCurrencyChange,
  currencyOptions = CURRENCIES,
}) => {
  const handleCurrencyChange = (event: SelectChangeEvent<string>) => {
    onCurrencyChange?.(event.target.value);
  };

  const formattedNetAmount = formatCurrency(details.netAmount, details.currency);

  return (
    <Box
      sx={{
        backgroundColor: '#fafafa',
        borderRadius: '8px',
        p: 2,
        mb: 3,
      }}
    >
      <Typography
        variant="subtitle1"
        sx={{ fontWeight: 600, mb: 2 }}
        component="h2"
      >
        {LABELS.cardTitle}
      </Typography>

      <Grid2 container spacing={1}>
        {/* Date */}
        <Grid2 size={{ xs: 6 }}>
          <Typography variant="body2" color="text.secondary">
            {LABELS.date}
          </Typography>
        </Grid2>
        <Grid2 size={{ xs: 6 }}>
          <Typography variant="body2" sx={{ textAlign: 'right' }}>
            {details.date}
          </Typography>
        </Grid2>

        {/* Patient Name */}
        <Grid2 size={{ xs: 6 }}>
          <Typography variant="body2" color="text.secondary">
            {LABELS.patientName}
          </Typography>
        </Grid2>
        <Grid2 size={{ xs: 6 }}>
          <Typography variant="body2" sx={{ textAlign: 'right' }}>
            {details.patientName}
          </Typography>
        </Grid2>

        {/* Patient ID / MRN */}
        <Grid2 size={{ xs: 6 }}>
          <Typography variant="body2" color="text.secondary">
            {LABELS.patientId}
          </Typography>
        </Grid2>
        <Grid2 size={{ xs: 6 }}>
          <Typography variant="body2" sx={{ textAlign: 'right' }}>
            {details.mrnNo ?? details.patientId}
          </Typography>
        </Grid2>

        {/* Currency */}
        <Grid2 size={{ xs: 6 }}>
          <Typography variant="body2" color="text.secondary">
            {LABELS.currency}
          </Typography>
        </Grid2>
        <Grid2 size={{ xs: 6 }}>
          <Select
            value={details.currency}
            onChange={handleCurrencyChange}
            size="small"
            disabled
            sx={{
              float: 'right',
              minWidth: 180,
              '& .MuiSelect-select': {
                py: 0.5,
                fontSize: '14px',
              },
            }}
            inputProps={{
              'aria-label': LABELS.currency,
            }}
          >
            {currencyOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </Grid2>

        {/* Net Amount */}
        <Grid2 size={{ xs: 6 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {LABELS.netAmount}
          </Typography>
        </Grid2>
        <Grid2 size={{ xs: 6 }}>
          <Typography variant="body2" sx={{ textAlign: 'right', fontWeight: 600, mt: 1 }}>
            {formattedNetAmount}
          </Typography>
        </Grid2>
      </Grid2>
    </Box>
  );
};

export default PaymentDetailsCard;